// Personnel roles (PMS-S178) — what a person DOES for an account, as opposed to
// `personnel.title`, which is their job title. PMS collapsed the old
// `account_contact` table and the flat ACORD contact columns onto personnel + a
// role join, so "add a contact" is now "add a person with a role".
//
// The enum and labels MIRROR the PMS helper (`lib/personnel-roles.ts`) so both
// portals name the same role the same way; the values are the contract enum in
// `docs/openapi.yaml`. Roles grant nothing on their own: PMS keys the LLC
// member/manager count and the credit-bureau pull on `ownershipPercentage`, so
// tagging someone `owner` without a share changes neither.

export type PersonnelRole =
  | "owner"
  | "executive_officer"
  | "accounting"
  | "inspection"
  | "claims"
  | "other";

export const PERSONNEL_ROLES: readonly PersonnelRole[] = [
  "owner",
  "executive_officer",
  "accounting",
  "inspection",
  "claims",
  "other",
];

export const PERSONNEL_ROLE_LABELS: Record<PersonnelRole, string> = {
  owner: "Owner",
  executive_officer: "Executive officer",
  accounting: "Accounting records",
  inspection: "Inspection",
  claims: "Claims",
  other: "Other",
};

/** A role's display label; an unrecognized value renders as itself rather than
 *  disappearing (the contract may add a role before this portal ships support). */
export function personnelRoleLabel(role: string): string {
  return PERSONNEL_ROLE_LABELS[role as PersonnelRole] ?? role;
}

/** Roles in canonical (enum) order, de-duplicated. PMS 400s a duplicated role
 *  within one person, so a write must never send one; the checkbox UI can't
 *  produce a duplicate, but a prefill or a stale server value could. */
export function normalizeRoles(roles: readonly string[]): PersonnelRole[] {
  return PERSONNEL_ROLES.filter((role) => roles.includes(role));
}

/**
 * The LLC member/manager count, DERIVED from the people holding an ownership
 * share. PMS-S178 dropped the stored `llcMemberCount` column precisely so the
 * number can't drift from the people it counts — note it keys on the ownership
 * share, NOT on the `owner` role.
 */
export function llcMemberCount(
  personnel: readonly { ownershipPercentage: string | null }[],
): number {
  return personnel.filter((p) => {
    const pct = Number(p.ownershipPercentage);
    return Number.isFinite(pct) && pct > 0;
  }).length;
}
